import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable, catchError, throwError } from 'rxjs';
import { HANDLED_ERROR_STATUSES } from '../common/table/table-resource';
import { ErrorService } from '../common/errors/error.service';

/**
 * Global HTTP error interceptor: routes every failed response through the single {@link ErrorService}
 * (which parses the RFC 9457 ProblemDetails body and picks the display tier) and then RETHROWS, so any
 * component `.catch()` still runs. This is the standard error surface — every request goes through it, so
 * components should NOT re-display HTTP errors with their own toast.
 *
 * Selective opt-out: a request whose {@link HANDLED_ERROR_STATUSES} context lists the failing status
 * handles that error inline itself (e.g. cfg-table's error state with retry) — the interceptor stays
 * silent for those statuses but still rethrows. Default-on global, explicit opt-out: a forgotten
 * opt-out is a visible duplicate message; a forgotten opt-in would be a silently swallowed error.
 * The default opt-out list lives in `tableResource` — hand-rolled opt-outs are a review smell.
 */
@Injectable()
export class GlobalErrorInterceptor implements HttpInterceptor {
  private readonly errorService = inject(ErrorService);

  intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    return next.handle(request).pipe(
      catchError((err: unknown) => {
        const handledByCaller = request.context.get(HANDLED_ERROR_STATUSES);
        const status = err instanceof HttpErrorResponse ? err.status : undefined;
        if (status === undefined || !handledByCaller.includes(status)) {
          this.errorService.handle(err);
        }
        // Rethrow unchanged — components that catch this error still receive the original HttpErrorResponse.
        return throwError(() => err);
      }),
    );
  }
}
